import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { PT_INTAKE_OPTIONS } from "../../lib/ptI18n";

/* PT Intake-formulier — draft wordt automatisch bewaard. Opties bewaren stabiele
   bestaande waarden; alleen de zichtbare labels schakelen mee met NL/EN. */
function ChoiceGroup({ selected, options, testid, onToggle }) {
  const { t } = useT();
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(([value, labelKey]) => (
        <button key={value} type="button" onClick={() => onToggle(value)}
          className="chip px-3 py-2 border transition-colors"
          style={selected.includes(value) ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
          data-testid={`${testid}-${value}`}>{t(labelKey)}</button>
      ))}
    </div>
  );
}

function SingleGroup({ value, options, testid, onSet }) {
  const { t } = useT();
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(([optionValue, labelKey]) => (
        <button key={optionValue} type="button" onClick={() => onSet(optionValue)}
          className="chip px-3 py-2 border transition-colors"
          style={value === optionValue ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
          data-testid={`${testid}-${optionValue}`}>{t(labelKey)}</button>
      ))}
    </div>
  );
}

function YesNoGroup({ value, testid, onSet }) {
  const { t } = useT();
  return (
    <div className="flex gap-2" data-testid={testid}>
      {[[true, "yes"], [false, "no"]].map(([optionValue, key]) => (
        <button key={key} type="button" onClick={() => onSet(optionValue)}
          className="chip px-4 py-2 border transition-colors"
          style={value === optionValue ? { background: "var(--joh-accent)", color: "#0A0A0A", borderColor: "var(--joh-accent)" } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
          data-testid={`${testid}-${key}`}>{t(`pt.intake.${key}`)}</button>
      ))}
    </div>
  );
}

function IntakeHeading({ children }) {
  return <div className="chip text-[#A3A3A3] mt-8 mb-3">— {children}</div>;
}

export default function PTIntake({ draft, onDone }) {
  const { t } = useT();
  const [f, setF] = useState({
    boxing_level: "", boxed_before: null, boxing_duration: "",
    goals: [], goals_other: "", main_goal: "",
    exercise_frequency: "", fitness_level: "", other_sport: null, other_sports: "",
    focus: [], train_frequency: "",
    physical_issues: null, physical_details: "", extra: "",
    ...(draft || {}),
  });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post("/pt/intake/draft", { data: f }).catch(() => {});
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [f]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, val) => setF((p) => ({ ...p, [k]: p[k].includes(val) ? p[k].filter((x) => x !== val) : [...p[k], val] }));

  const validate = () => {
    const e = [];
    if (!f.boxing_level) e.push(t("pt.intake.err.level"));
    if (f.boxed_before === null) e.push(t("pt.intake.err.boxedBefore"));
    if (f.boxed_before && !f.boxing_duration) e.push(t("pt.intake.err.duration"));
    if (!f.goals.length) e.push(t("pt.intake.err.goals"));
    if (f.goals.includes("Other") && !f.goals_other.trim()) e.push(t("pt.intake.err.other"));
    if (!f.main_goal.trim()) e.push(t("pt.intake.err.mainGoal"));
    if (!f.exercise_frequency) e.push(t("pt.intake.err.frequency"));
    if (!f.fitness_level) e.push(t("pt.intake.err.fitness"));
    if (f.other_sport === null) e.push(t("pt.intake.err.otherSport"));
    if (f.other_sport && !f.other_sports.trim()) e.push(t("pt.intake.err.sports"));
    if (f.physical_issues === null) e.push(t("pt.intake.err.physical"));
    if (f.physical_issues && !f.physical_details.trim()) e.push(t("pt.intake.err.physicalDetails"));
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (errs.length) { toast.error(t("pt.intake.missing")); return; }
    setBusy(true);
    try {
      await api.post("/pt/intake", f);
      toast.success(t("pt.intake.success"));
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pt.intake.failed"));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} data-testid="pt-intake-form">
      {errors.length > 0 && (
        <div className="border border-[#F87171]/50 p-4 mb-6" data-testid="pt-intake-errors">
          <div className="chip text-[#F87171] mb-2">{t("pt.intake.check")}</div>
          {errors.map((error, i) => <div key={i} className="text-sm text-[#DDD6CB]">· {error}</div>)}
        </div>
      )}

      <IntakeHeading>{t("pt.intake.heading.experience").toUpperCase()}</IntakeHeading>
      <div className="chip text-[#DDD6CB] mb-2">{t("pt.intake.q.level")}*</div>
      <SingleGroup value={f.boxing_level} options={PT_INTAKE_OPTIONS.levels} testid="pt-intake-level" onSet={(option) => set("boxing_level", option)} />
      <div className="chip text-[#DDD6CB] mt-5 mb-2">{t("pt.intake.q.boxedBefore")}*</div>
      <YesNoGroup value={f.boxed_before} testid="pt-intake-boxed" onSet={(v) => set("boxed_before", v)} />
      {f.boxed_before && (
        <>
          <div className="chip text-[#DDD6CB] mt-5 mb-2">{t("pt.intake.q.duration")}*</div>
          <SingleGroup value={f.boxing_duration} options={PT_INTAKE_OPTIONS.durations} testid="pt-intake-duration" onSet={(option) => set("boxing_duration", option)} />
        </>
      )}

      <IntakeHeading>{t("pt.intake.heading.goals").toUpperCase()}</IntakeHeading>
      <div className="chip text-[#DDD6CB] mb-2">{t("pt.intake.q.goals")}* ({t("pt.intake.q.multiple")})</div>
      <ChoiceGroup selected={f.goals} options={PT_INTAKE_OPTIONS.goals} testid="pt-intake-goals" onToggle={(option) => toggle("goals", option)} />
      {f.goals.includes("Other") && (
        <textarea rows={2} value={f.goals_other} onChange={(e) => set("goals_other", e.target.value)} placeholder={t("pt.intake.ph.explain")} className="editorial-input dark w-full mt-3" data-testid="pt-intake-goals-other" />
      )}
      <div className="chip text-[#DDD6CB] mt-5 mb-2">{t("pt.intake.q.mainGoal")}*</div>
      <textarea rows={2} value={f.main_goal} onChange={(e) => set("main_goal", e.target.value)} className="editorial-input dark w-full" data-testid="pt-intake-maingoal" />

      <IntakeHeading>{t("pt.intake.heading.current").toUpperCase()}</IntakeHeading>
      <div className="chip text-[#DDD6CB] mb-2">{t("pt.intake.q.exerciseFrequency")}*</div>
      <SingleGroup value={f.exercise_frequency} options={PT_INTAKE_OPTIONS.frequency} testid="pt-intake-freq" onSet={(option) => set("exercise_frequency", option)} />
      <div className="chip text-[#DDD6CB] mt-5 mb-2">{t("pt.intake.q.fitness")}*</div>
      <SingleGroup value={f.fitness_level} options={PT_INTAKE_OPTIONS.fitness} testid="pt-intake-fitness" onSet={(option) => set("fitness_level", option)} />
      <div className="chip text-[#DDD6CB] mt-5 mb-2">{t("pt.intake.q.otherSport")}*</div>
      <YesNoGroup value={f.other_sport} testid="pt-intake-sport" onSet={(v) => set("other_sport", v)} />
      {f.other_sport && (
        <input value={f.other_sports} onChange={(e) => set("other_sports", e.target.value)} placeholder={t("pt.intake.ph.sports")} className="editorial-input dark w-full mt-3" data-testid="pt-intake-sports" />
      )}

      <IntakeHeading>{t("pt.intake.heading.preferences").toUpperCase()}</IntakeHeading>
      <div className="chip text-[#DDD6CB] mb-2">{t("pt.intake.q.focus")}</div>
      <ChoiceGroup selected={f.focus} options={PT_INTAKE_OPTIONS.focus} testid="pt-intake-focus" onToggle={(option) => toggle("focus", option)} />
      <div className="chip text-[#DDD6CB] mt-5 mb-2">{t("pt.intake.q.trainFrequency")}</div>
      <SingleGroup value={f.train_frequency} options={PT_INTAKE_OPTIONS.trainFrequency} testid="pt-intake-trainfreq" onSet={(option) => set("train_frequency", option)} />

      <IntakeHeading>{t("pt.intake.heading.physical").toUpperCase()}</IntakeHeading>
      <div className="chip text-[#DDD6CB] mb-2">{t("pt.intake.q.physical")}*</div>
      <YesNoGroup value={f.physical_issues} testid="pt-intake-physical" onSet={(v) => set("physical_issues", v)} />
      {f.physical_issues && (
        <>
          <textarea rows={2} value={f.physical_details} onChange={(e) => set("physical_details", e.target.value)} placeholder={t("pt.intake.ph.physical")} className="editorial-input dark w-full mt-3" data-testid="pt-intake-physical-details" />
          <p className="chip text-[#4A4A4A] mt-2">{t("pt.intake.ph.privacy")}</p>
        </>
      )}

      <IntakeHeading>{t("pt.intake.heading.extra").toUpperCase()}</IntakeHeading>
      <div className="chip text-[#DDD6CB] mb-2">{t("pt.intake.q.extra")}</div>
      <textarea rows={3} value={f.extra} onChange={(e) => set("extra", e.target.value)} className="editorial-input dark w-full" data-testid="pt-intake-extra" />

      <button type="submit" disabled={busy} className="w-full py-4 chip mt-8" style={{ background: "var(--joh-accent)", color: "#0A0A0A" }} data-testid="pt-intake-submit">
        {busy ? "…" : t("pt.intake.submit").toUpperCase()}
      </button>
    </form>
  );
}
